import { NativeSpace, nativeBackendUrl } from '../../../environments/backend-endpoints';

/**
 * The tenants (spaces) the native app knows. In the browser the page origin
 * names the tenant; the app keeps its own list instead and always starts in
 * the space chosen last. Switching reloads the page, so every store starts
 * clean in the new space.
 */
export interface Space extends NativeSpace {
  title: string;
  darkLogo: string | null;
  lightLogo: string | null;
}

interface SpaceList {
  spaces: Space[];
  active: string | null;
}

const STORAGE_KEY = 'andorSpaces';

export function loadSpaces(): SpaceList {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as SpaceList | null;
    if (stored && Array.isArray(stored.spaces)) {
      const spaces = stored.spaces.filter(reachable);
      const active = spaces.some((s) => s.domain === stored.active) ? stored.active : null;
      return { spaces, active };
    }
  } catch {
    // A broken entry is treated like none: the app asks for a space again.
  }
  return { spaces: [], active: null };
}

/** An entry this build can't talk to (broken, or from an older format) is
 * left out, so the app asks for a space instead of failing to start. */
function reachable(space: Space): boolean {
  try {
    nativeBackendUrl(space, {});
    return true;
  } catch {
    return false;
  }
}

function save(list: SpaceList): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

/** The space the app starts in; `null` until one was added. */
export function activeSpace(): Space | null {
  const { spaces, active } = loadSpaces();
  return spaces.find((s) => s.domain === active) ?? null;
}

/** Adds or refreshes `space` and makes it the active one. */
export function addSpace(space: Space): void {
  const { spaces } = loadSpaces();
  save({
    spaces: [...spaces.filter((s) => s.domain !== space.domain), space],
    active: space.domain,
  });
}

export function selectSpace(domain: string): void {
  save({ ...loadSpaces(), active: domain });
}

/**
 * The space's app moved to `to` (renamed address): keep its entry, now under
 * the new domain, and its active state. Sign-ins stay with the old domain.
 */
export function renameSpace(from: string, to: string): void {
  const { spaces, active } = loadSpaces();
  const moved = spaces.find((s) => s.domain === from);
  if (!moved) return;
  save({
    spaces: [
      ...spaces.filter((s) => s.domain !== from && s.domain !== to),
      { ...moved, domain: to },
    ],
    active: active === from ? to : active,
  });
}

/** Removing the active space leaves none active: the app asks again. */
export function removeSpace(domain: string): void {
  const { spaces, active } = loadSpaces();
  save({
    spaces: spaces.filter((s) => s.domain !== domain),
    active: active === domain ? null : active,
  });
}

/**
 * The space in what someone typed or pasted: a bare domain, an origin or a
 * full invite link. `null` for anything that can't name a space (ports,
 * credentials, other protocols).
 *
 * Given a `localServer` (development builds), `localhost` and `*.localhost`
 * name instances of the local andor-server, each its own tenant, reached
 * through `localServer` whatever port was typed.
 */
export function parseSpaceAddress(input: string, localServer?: string | null): NativeSpace | null {
  const value = input.trim();
  if (!value) return null;
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`);
    if (!['http:', 'https:'].includes(url.protocol) || url.username) return null;
    const domain = url.hostname.toLowerCase();
    if (
      !/^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/.test(
        domain
      )
    ) {
      return null;
    }
    if (domain === 'localhost' || domain.endsWith('.localhost')) {
      return localServer ? { domain, server: localServer } : null;
    }
    return url.port ? null : { domain };
  } catch {
    return null;
  }
}
